import { AutoAdvanceRow } from "@/components/AutoAdvance";
import { ArrowDown, BarChart3, CheckCircle2, Clock, Eye, Flag, LoaderCircle, Mail, Megaphone, Pencil, Play, Power, RefreshCw, Save, Send, Sparkles, Users, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const excerpt = (text, max = 220) => (text || "").replace(/\s+/g, " ").trim().slice(0, max) + ((text || "").length > max ? "…" : "");

function nextMonday8() {
  const d = new Date();
  d.setHours(8, 0, 0, 0);
  do { d.setDate(d.getDate() + 1); } while (d.getDay() !== 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T08:00`;
}

function fmtSchedule(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("fr-FR", { weekday: "long", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function BroadcastPanel() {
  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState([]);
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState("");
  const [brief, setBrief] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachPdf, setAttachPdf] = useState(false);
  const [schedule, setSchedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState(nextMonday8());
  const [variants, setVariants] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSteps = () => api.get("/broadcast/steps").then(({ data }) => setSteps(data.steps || [])).catch(() => {});
  useEffect(() => { if (open && steps.length === 0) loadSteps(); }, [open, steps.length]);

  const toggle = (id) => { setPreview(null); setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])); };
  const selectedCount = steps.filter((s) => selected.includes(s.step_id)).reduce((a, s) => a + (s.count || 0), 0);

  const generate = async () => {
    if (!brief.trim()) { toast.error("Décrivez d’abord l’objectif de l’e-mail."); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/email-draft", { context: "automation", instruction: brief }, { timeout: 120000 });
      setVariants(data.variants || []);
      if (data.variants?.[0]) { setSubject(data.variants[0].subject); setBody(data.variants[0].body); }
      toast.success("3 propositions générées. Choisissez-en une, puis ajustez librement.");
    } catch (error) { toast.error(error.response?.data?.detail || "Génération IA impossible."); }
    finally { setGenerating(false); }
  };

  const runPreview = async () => {
    if (selected.length === 0) { toast.error("Sélectionnez au moins une étape."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/broadcast/preview", { step_ids: selected }, { timeout: 120000 });
      setPreview(data);
    } catch (error) { toast.error(error.response?.data?.detail || "Aperçu impossible."); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!name.trim()) { toast.error("Donnez un nom à la campagne."); return; }
    if (selected.length === 0) { toast.error("Sélectionnez au moins une étape."); return; }
    if (!subject.trim() || !body.trim()) { toast.error("Objet et corps de l’e-mail requis."); return; }
    if (schedule && !scheduleAt) { toast.error("Choisissez une date de démarrage."); return; }
    const when = schedule ? ` L’envoi démarrera ${fmtSchedule(scheduleAt)}.` : " L’envoi se fait au rythme de la File d’envoi.";
    if (!window.confirm(`Envoyer cette campagne à ${preview ? preview.with_email : selectedCount} fiche(s) ?${when}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/broadcast/send", { name, step_ids: selected, subject, body, attach_pdf: attachPdf, scheduled_at: schedule ? scheduleAt : null }, { timeout: 180000 });
      toast.success(schedule ? `Campagne programmée : ${data.queued} e-mail(s) partiront ${fmtSchedule(scheduleAt)}.` : `Campagne lancée : ${data.queued} e-mail(s) mis dans la file d’envoi.`);
      setName(""); setBrief(""); setSubject(""); setBody(""); setVariants([]); setSelected([]); setPreview(null); setSchedule(false); setScheduleAt(nextMonday8());
    } catch (error) { toast.error(error.response?.data?.detail || "Envoi impossible."); }
    finally { setBusy(false); }
  };

  return (
    <div className="broadcast-panel" data-testid="broadcast-panel">
      <button type="button" className="broadcast-toggle" onClick={() => setOpen((v) => !v)} data-testid="broadcast-toggle">
        <span><Megaphone size={17} /> Campagne groupée — envoyer un e-mail à toutes les fiches de plusieurs étapes</span>
        <ArrowDown size={16} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
      </button>
      {open && (
        <div className="broadcast-body">
          <label className="broadcast-label">1 · Nom de la campagne</label>
          <input className="broadcast-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Relance estivale juin 2026" data-testid="broadcast-name" />

          <label className="broadcast-label">2 · Étapes ciblées <span className="broadcast-hint">({selected.length} sélectionnée(s) · {selectedCount} opportunités)</span></label>
          <div className="broadcast-steps" data-testid="broadcast-steps">
            {steps.map((s) => (
              <button type="button" key={s.step_id} className={`broadcast-step${selected.includes(s.step_id) ? " is-on" : ""}`} onClick={() => toggle(s.step_id)} data-testid={`broadcast-step-${s.step_id}`}>
                <span>{selected.includes(s.step_id) ? <CheckCircle2 size={14} /> : <span className="broadcast-dot" />} {s.name}</span>
                <b>{s.count}</b>
              </button>
            ))}
          </div>

          <label className="broadcast-label">3 · Rédaction assistée par l’IA</label>
          <textarea className="broadcast-input" rows={2} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Décrivez l’objectif en une phrase (ex. « Relancer les mairies sur l’offre climatiseurs EDF avant la fin de l’été, ton chaleureux »)" data-testid="broadcast-brief" />
          <button type="button" className="secondary-button" onClick={generate} disabled={generating} data-testid="broadcast-generate">
            {generating ? <LoaderCircle size={16} className="spin" /> : <Sparkles size={16} />} Générer 3 propositions
          </button>
          {variants.length > 0 && (
            <div className="broadcast-variants">
              {variants.map((v, i) => (
                <button type="button" key={i} className={`broadcast-variant${subject === v.subject ? " is-on" : ""}`} onClick={() => { setSubject(v.subject); setBody(v.body); }} data-testid={`broadcast-variant-${i}`}>{v.label}</button>
              ))}
            </div>
          )}
          <input className="broadcast-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Objet de l’e-mail" data-testid="broadcast-subject" />
          <textarea className="broadcast-input" rows={8} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Corps de l’e-mail — variables : {commune}, {date}, {signature_nom}…" data-testid="broadcast-bodytext" />
          <label className="broadcast-check"><input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} data-testid="broadcast-attach" /> Joindre le dernier devis PDF de chaque fiche</label>

          <div className="broadcast-schedule" data-testid="broadcast-schedule">
            <label className="broadcast-check">
              <input type="checkbox" checked={schedule} onChange={(e) => setSchedule(e.target.checked)} data-testid="broadcast-schedule-toggle" />
              <Clock size={15} /> Programmer l’envoi (sinon démarrage immédiat au rythme de la file)
            </label>
            {schedule && (
              <div className="broadcast-schedule-row">
                <input type="datetime-local" className="broadcast-input" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} data-testid="broadcast-schedule-at" />
                <span className="broadcast-hint">Les e-mails sont mis en file dès maintenant mais ne partiront qu’à partir de cette date (heure de Paris).{scheduleAt ? ` Démarrage : ${fmtSchedule(scheduleAt)}.` : ""}</span>
              </div>
            )}
          </div>

          <div className="broadcast-actions">
            <button type="button" className="secondary-button" onClick={runPreview} disabled={busy} data-testid="broadcast-preview">
              {busy ? <LoaderCircle size={16} className="spin" /> : <Eye size={16} />} Aperçu des destinataires
            </button>
            <button type="button" className="primary-button" onClick={send} disabled={busy} data-testid="broadcast-send">
              <Send size={16} /> Envoyer la campagne
            </button>
          </div>
          {preview && (
            <p className="broadcast-preview" data-testid="broadcast-preview-result">
              <Users size={15} /> <b>{preview.total}</b> opportunités · <b>{preview.with_email}</b> avec e-mail valide · <b>{preview.without_email}</b> sans e-mail (basculées en « Mail non envoyé »).
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function CampaignTracker() {
  const [campaigns, setCampaigns] = useState(null);
  const [queue, setQueue] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/broadcast/campaigns")
    .then(({ data }) => { setCampaigns(data.campaigns || []); setQueue(data.queue || null); })
    .catch(() => {});

  useEffect(() => {
    load();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, []);

  if (!campaigns || campaigns.length === 0) return null;

  const fmtDate = (v) => (v ? new Date(v).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

  return (
    <div className="campaign-tracker" data-testid="campaign-tracker">
      <div className="campaign-tracker-head">
        <span><BarChart3 size={16} /> Suivi des campagnes groupées</span>
        {queue && (
          <span className="campaign-queue-state" data-testid="campaign-queue-state">
            {queue.paused ? "File en pause" : queue.in_window ? `Envoi en cours · ${queue.paris_time} Paris` : `Hors plage horaire · ${queue.paris_time} Paris`}
            {typeof queue.sent_today === "number" ? ` · ${queue.sent_today}/${queue.daily_cap} aujourd’hui` : ""}
          </span>
        )}
      </div>
      <div className="campaign-list">
        {campaigns.map((c) => {
          const total = c.total || (c.sent + c.failed + c.pending) || 0;
          const done = c.sent + c.failed;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          const running = c.pending > 0;
          const scheduled = running && c.scheduled_at && new Date(c.scheduled_at) > new Date();
          return (
            <div key={c.id} className={`campaign-row${running ? " is-running" : ""}`} data-testid={`campaign-row-${c.id}`}>
              <div className="campaign-row-top">
                <div className="campaign-row-name">
                  <strong data-testid={`campaign-name-${c.id}`}>{c.name}</strong>
                  <small>{(c.step_names || []).join(" · ")} · lancée le {fmtDate(c.created_at)}</small>
                </div>
                <span className={`campaign-badge${scheduled ? " scheduled" : running ? " running" : " done"}`} data-testid={`campaign-badge-${c.id}`}>
                  {scheduled ? <Clock size={12} /> : running ? <LoaderCircle size={12} className="spinning" /> : <CheckCircle2 size={12} />} {scheduled ? "Programmée" : running ? "En cours" : "Terminée"}
                </span>
              </div>
              <div className="campaign-bar" data-testid={`campaign-bar-${c.id}`}>
                <div className="campaign-bar-fill" style={{ width: `${pct}%` }} />
              </div>
              <div className="campaign-row-stats" data-testid={`campaign-stats-${c.id}`}>
                {scheduled ? (
                  <span className="cs-eta" data-testid={`campaign-scheduled-${c.id}`}>Démarre {fmtSchedule(c.scheduled_at)}</span>
                ) : (
                  <>
                    <span className="cs-sent">{c.sent} envoyé{c.sent > 1 ? "s" : ""}</span>
                    <span className="cs-pending">{c.pending} en attente</span>
                    {c.failed > 0 && <span className="cs-failed">{c.failed} échec{c.failed > 1 ? "s" : ""}</span>}
                    <span className="cs-total">{done}/{total} · {pct}%</span>
                    {running && queue?.eta && <span className="cs-eta">Fin estimée : {queue.eta}</span>}
                  </>
                )}
                <button type="button" className="link-button" onClick={() => navigate("/envois")} data-testid={`campaign-detail-${c.id}`}>Détail dans la file d’envoi</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function RelancePathPage() {
  const [data, setData] = useState(null);
  const [delays, setDelays] = useState({});
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(null);
  const [confirmAll, setConfirmAll] = useState(null);
  const [startPreview, setStartPreview] = useState(null);
  const [startPreviewLoading, setStartPreviewLoading] = useState(false);

  const previewStart = async () => {
    setStartPreviewLoading(true);
    try {
      const { data: response } = await api.get("/relance-path/start-preview", { timeout: 120000 });
      setStartPreview(response);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Aperçu impossible.");
    } finally {
      setStartPreviewLoading(false);
    }
  };

  const runStart = async () => {
    try {
      const { data: response } = await api.post("/relance-path/start-run", {}, { timeout: 120000 });
      toast.success(response.message);
      setStartPreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Lancement impossible.");
    }
  };
  const navigate = useNavigate();

  const load = async () => {
    const { data: payload } = await api.get("/relance-path");
    setData(payload);
    const next = { start: { days: payload.start?.days || 3, enabled: Boolean(payload.start?.enabled) } };
    payload.steps.forEach((step) => {
      if (step.rule) next[step.rule.id] = { days: step.rule.auto_advance_days || 3, enabled: Boolean(step.rule.auto_advance_enabled) };
    });
    setDelays(next);
  };

  useEffect(() => {
    load().catch((error) => toast.error(error.response?.data?.detail || "Chargement du parcours impossible."));
  }, []);

  const saveDelays = async (override) => {
    setSaving(true);
    try {
      const items = data.steps.filter((step) => step.rule).map((step) => {
        const state = override ? override(step) : delays[step.rule.id];
        return { rule_id: step.rule.id, days: Number(state.days) || 3, enabled: state.enabled, next_step_id: step.next_step_id, next_step_name: step.next_step_name || "" };
      });
      const startStep = data.steps.find((step) => step.kind === "start");
      const startState = override ? override({ rule: { id: "start" } }) : delays.start;
      const start = startStep && startStep.next_step_id ? { days: Number(startState?.days) || 3, enabled: Boolean(startState?.enabled), next_step_id: startStep.next_step_id, next_step_name: startStep.next_step_name || "" } : null;
      const { data: response } = await api.patch("/relance-path/delays", { items, start });
      toast.success(response.message);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Enregistrement impossible.");
    } finally {
      setSaving(false);
      setConfirmAll(null);
    }
  };

  const draft = async (step) => {
    setDrafting(step.step_id);
    try {
      const { data: response } = await api.post("/relance-path/draft", { step_id: step.step_id, step_name: step.name }, { timeout: 180000 });
      toast.success(response.message);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rédaction IA impossible.");
    } finally {
      setDrafting(null);
    }
  };

  const [togglingId, setTogglingId] = useState(null);
  const toggleActive = async (rule, active) => {
    setTogglingId(rule.id);
    try {
      const { data: response } = await api.patch(`/relance-path/steps/${rule.id}/active`, { active });
      toast.success(response.message);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Le changement d’état a échoué.");
    } finally {
      setTogglingId(null);
    }
  };

  if (!data) return <section className="page">Chargement du parcours de relance…</section>;

  const allEnabled = data.steps.filter((s) => s.rule && s.next_step_id).every((s) => delays[s.rule.id]?.enabled) && Boolean(delays.start?.enabled);
  const rulesCount = data.steps.filter((s) => s.rule).length;

  return (
    <section className="page" data-testid="relance-path-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">SÉQUENCE DE RELANCES</p>
          <h1 data-testid="relance-path-title">Parcours de relance</h1>
          <p className="page-subtitle">Toute la chaîne en un coup d’œil : e-mails de chaque étape et délais entre les étapes. Les délais se comptent en jours ouvrés après l’envoi effectif de l’e-mail ; le passage s’exécute chaque jour ouvré à 8h.</p>
        </div>
        <div className="button-row">
          <button type="button" className={allEnabled ? "secondary-button" : "primary-button"} onClick={() => setConfirmAll(allEnabled ? "off" : "on")} disabled={saving || rulesCount === 0} data-testid="path-toggle-all">
            <Zap size={16} /> {allEnabled ? "Désactiver l’enchaînement" : "Activer l’enchaînement partout"}
          </button>
          <button type="button" className="icon-action" onClick={load} aria-label="Actualiser" data-testid="path-refresh"><RefreshCw size={18} /></button>
        </div>
      </header>

      <BroadcastPanel />
      <CampaignTracker />

      <div className="path-timeline" data-testid="path-timeline">
        {data.steps.map((step, index) => {
          const rule = step.rule;
          const state = rule ? delays[rule.id] : null;
          const hasNext = Boolean(step.next_step_id);
          return (
            <div key={step.step_id} className="path-item" data-testid={`path-step-${step.step_id}`}>
              <div className={`path-card kind-${step.kind}${rule && step.kind === "relance" && !rule.is_active ? " is-disabled" : ""}${rule ? "" : step.kind === "relance" ? " is-missing" : ""}`}>
                <div className="path-card-head">
                  <span className="path-step-name">
                    {step.kind === "start" ? <Mail size={15} /> : step.kind === "end" ? <Flag size={15} /> : <CheckCircle2 size={15} />} {step.name}
                    {step.kind === "relance" && rule && !rule.is_active && <span className="path-off-badge" data-testid={`path-off-badge-${step.step_id}`}>Désactivée</span>}
                  </span>
                  <span className="path-counts" data-testid={`path-count-${step.step_id}`}>
                    {step.count} fiche{step.count > 1 ? "s" : ""}{rule ? ` · ${step.pending} en attente · ${step.sent} envoyé${step.sent > 1 ? "s" : ""}` : ""}
                  </span>
                </div>
                {step.kind === "start" && (
                  <>
                    <p className="path-note">Point de départ : le devis a été envoyé automatiquement à la mairie. Sans réponse après le délai ci-dessous, la fiche passe en {step.next_step_name} et reçoit l’e-mail de cette étape.</p>
                    <div className="path-actions">
                      <button type="button" className="link-button" onClick={previewStart} disabled={startPreviewLoading} data-testid="path-start-preview">
                        {startPreviewLoading ? <LoaderCircle size={12} className="spinning" /> : <Eye size={12} />} Aperçu des fiches concernées
                      </button>
                      {startPreview && (
                        <span className="advance-preview" data-testid="path-start-preview-result">
                          <strong>{startPreview.count}</strong> fiche(s) seraient déplacées vers « {startPreview.to_step} »
                          {startPreview.count > 0 && <button type="button" className="link-button" onClick={runStart} data-testid="path-start-run"><Play size={12} /> Lancer maintenant</button>}
                        </span>
                      )}
                    </div>
                  </>
                )}
                {step.kind === "end" && <p className="path-note">Fin de séquence : la fiche s’arrête ici, aucun e-mail n’est envoyé.</p>}
                {step.kind === "relance" && rule && (
                  <>
                    <p className="path-subject" data-testid={`path-subject-${step.step_id}`}><strong>Objet :</strong> {rule.email_subject}</p>
                    <p className="path-excerpt">{excerpt(rule.email_body)}</p>
                    {!rule.is_active && <p className="path-off-note" data-testid={`path-off-note-${step.step_id}`}>Relance désactivée : aucun e-mail ne part à cette étape. Les fiches continuent quand même vers l’étape suivante après le délai.</p>}
                    <div className="path-actions">
                      <button type="button" className={rule.is_active ? "secondary-button path-toggle-off" : "primary-button"} onClick={() => toggleActive(rule, !rule.is_active)} disabled={togglingId === rule.id} data-testid={`path-toggle-active-${step.step_id}`}>
                        <Power size={14} /> {togglingId === rule.id ? "…" : rule.is_active ? "Désactiver cette relance" : "Réactiver cette relance"}
                      </button>
                      <button type="button" className="secondary-button" onClick={() => navigate(`/automatisations?edit=${rule.id}`)} data-testid={`path-edit-${step.step_id}`}><Pencil size={14} /> Modifier l’e-mail</button>
                      <span className="path-meta">{rule.variants?.length || 0} variante(s) anti-spam{rule.attach_pdf ? " · devis PDF joint" : ""}{rule.webhook_error ? " · ⚠ webhook non branché" : ""}</span>
                    </div>
                  </>
                )}
                {step.kind === "relance" && rule && rule.auto_advance_step_id && (
                  <AutoAdvanceRow rule={rule} onDone={load} />
                )}
                {step.kind === "relance" && !rule && (
                  <div className="path-missing">
                    <p>Aucun e-mail défini pour cette étape : les fiches qui y arrivent ne reçoivent rien.</p>
                    <div className="button-row">
                      <button type="button" className="primary-button" onClick={() => draft(step)} disabled={drafting !== null} data-testid={`path-draft-${step.step_id}`}>
                        {drafting === step.step_id ? <LoaderCircle size={14} className="spinning" /> : <Sparkles size={14} />} {drafting === step.step_id ? "Rédaction en cours…" : "Rédiger avec l’IA"}
                      </button>
                      <button type="button" className="secondary-button" onClick={() => navigate("/automatisations")} data-testid={`path-manual-${step.step_id}`}><Pencil size={14} /> Écrire moi-même</button>
                    </div>
                  </div>
                )}
              </div>
              {hasNext && (
                <div className="path-connector" data-testid={`path-delay-${step.step_id}`}>
                  <ArrowDown size={16} />
                  {rule || step.kind === "start" ? (
                    <label className={`path-delay${(rule ? state : delays.start)?.enabled ? "" : " is-off"}`}>
                      <input type="checkbox" checked={Boolean((rule ? state : delays.start)?.enabled)} onChange={(e) => setDelays({ ...delays, [rule ? rule.id : "start"]: { ...(rule ? state : delays.start), enabled: e.target.checked } })} data-testid={`path-delay-enabled-${step.step_id}`} />
                      <span>après</span>
                      <input type="number" min="1" max="60" value={(rule ? state : delays.start)?.days ?? 3} onChange={(e) => setDelays({ ...delays, [rule ? rule.id : "start"]: { ...(rule ? state : delays.start), days: e.target.value } })} data-testid={`path-delay-days-${step.step_id}`} />
                      <span>j ouvré(s) sans réponse → <strong>{step.next_step_name}</strong>{(rule ? state : delays.start)?.enabled ? "" : " (désactivé)"}</span>
                    </label>
                  ) : (
                    <span className="path-delay is-off"><span>Délai réglable une fois l’e-mail défini → <strong>{step.next_step_name}</strong></span></span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="button-row path-save">
        <button type="button" className="primary-button" onClick={() => saveDelays()} disabled={saving || rulesCount === 0} data-testid="path-save-delays"><Save size={16} /> {saving ? "Enregistrement…" : "Enregistrer les délais"}</button>
        <button type="button" className="secondary-button" onClick={() => { const next = {}; Object.keys(delays).forEach((id) => { next[id] = { ...delays[id], days: 3 }; }); setDelays(next); }} data-testid="path-apply-3-days">Mettre 3 jours partout</button>
      </div>

      <ConfirmDialog
        open={Boolean(confirmAll)}
        title={confirmAll === "on" ? "Activer l’enchaînement automatique sur toute la chaîne ?" : "Désactiver l’enchaînement automatique ?"}
        description={confirmAll === "on" ? "Dès demain 8h, toutes les fiches sans réponse depuis le délai indiqué — à partir de « Devis envoyé » — passeront à l’étape suivante dans noCRM et recevront l’e-mail correspondant via la file d’envoi lissée." : "Plus aucune fiche ne sera déplacée automatiquement. Les délais restent enregistrés."}
        confirmLabel={confirmAll === "on" ? "Activer" : "Désactiver"}
        onConfirm={() => saveDelays((step) => ({ ...delays[step.rule.id], enabled: confirmAll === "on" }))}
        onCancel={() => setConfirmAll(null)}
      />
    </section>
  );
}
